import { isEmpty } from 'lodash';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { showToast } from '~/components/Shared/EcToast/showToast';
import { Card } from '~/eds/src/components/Card/Card';
import { useModal } from '~/eds/src/components/hooks/useModal';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Link } from '~/eds/src/components/Link/Link';
import { Text } from '~/eds/src/components/Text/Text';
import { AutomationStageType } from '~/enums/AutomationStageType';
import { QueryParamType } from '~/enums/QueryParamType';
import { FormattedMessage, useIntl } from 'react-intl';
import { buildQuery } from '~/redux/api/methods/search';
import { api } from '~/redux/api/createApi';
import { slices } from '~/redux/slices';
import { ERROR, SUCCESS } from '~/types/toast.types';
import LoadingContainer from '~/ui/components/LoadingContainer/LoadingContainer';
import { getPageSearchQueryByKey } from '~/utils/searchQuery';

import AccuracyMetrics from '../../AutomationHubGuidedAiPage/Content/AccuracyMetrics';
import AutomationHubSearchingFilters from '../../AutomationHubSearchFilters/AutomationHubSearchingFilters';
import {
  ACCURACY_METRIC_TRAINING_STAGES,
  getTrainingStages,
} from '../../constants';

const GuidedAiStatus = () => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const TRAINING_STAGES = getTrainingStages(intl);
  const model = useSelector(slices.automation.selectors.selectAutomationModel);

  const manage = useSelector(
    slices.automation.selectors.selectAutomationManage,
  );
  const filters = useSelector(
    slices.automation.selectors.selectAutomationFilters,
  );
  const fields = useSelector(
    slices.automation.selectors.selectAutomationFields,
  );

  const stage = model?.stage;

  const isInViewMode = manage === AutomationStageType.ViewStatus;
  const currentMetrics = model?.current?.accuracyMetrics;
  const hasCurrentMetrics = !isEmpty(currentMetrics);

  const clauseId = getPageSearchQueryByKey(QueryParamType.clauseId, model.id);

  const [updatePredictionFilters, updatePredictionFiltersResults] =
    api.endpoints.updatePredictionFilters.useMutation();
  const {
    isError: isUpdatePredictionFiltersError,
    isSuccess: isUpdatePredictionFiltersSuccess,
    isLoading: isUpdatePredictionFiltersLoading,
  } = updatePredictionFiltersResults;

  const [modal, show, hide] = useModal({
    disableHideOnEscape: true,
    children: (
      <Layout direction="column" spacing={2}>
        <Text>
          {intl.formatMessage(
            {
              id: 'GuidedAiStatus.modalIntroText',
              defaultMessage:
                'You trained your model using All Types of contracts. What contracts would you like your model to tag with the {clauseName} clause?',
              description: 'Intro text for prediction filter modal',
            },
            { clauseName: model.name },
          )}
        </Text>
        <AutomationHubSearchingFilters prediction />
      </Layout>
    ),
    primaryAction: {
      isLoading: isUpdatePredictionFiltersLoading,
      text: intl.formatMessage({
        id: 'GuidedAiStatus.modalDoneButton',
        defaultMessage: 'Done',
        description: 'Done button text for prediction filter modal',
      }),
      variant: 'primary',
      onClick: () => {
        const filterQuery = buildQuery({
          filters: filters.filter((f) => !!f.operatorId),
          fields,
          booleanQuery: '',
        });

        return updatePredictionFilters({
          clauseId,
          predictionFilters: filterQuery,
        });
      },
    },
    title: intl.formatMessage({
      id: 'GuidedAiStatus.modalTitle',
      defaultMessage: 'Apply Model to Specific Contracts',
      description: 'Title for prediction filter modal',
    }),
    onCancel: () =>
      dispatch(slices.automation.actions.resetAutomationFilters()),
    onHide: () => dispatch(slices.automation.actions.resetAutomationFilters()),
  });

  useEffect(() => {
    if (isUpdatePredictionFiltersSuccess) {
      const message = intl.formatMessage({
        id: 'GuidedAiStatus.updatePredictionSuccess',
        defaultMessage: 'Update prediction filter successfully.',
        description: 'Success message for updating prediction filter',
      });
      showToast(SUCCESS, message);

      hide();
      dispatch(slices.automation.actions.resetAutomationFilters());
    }
    if (isUpdatePredictionFiltersError) {
      const message = intl.formatMessage({
        id: 'GuidedAiStatus.updatePredictionError',
        defaultMessage: 'Update prediction filter failed. Please try again.',
        description: 'Error message for updating prediction filter',
      });

      showToast(ERROR, message);
    }
  }, [isUpdatePredictionFiltersSuccess, isUpdatePredictionFiltersError]);

  if (!model?.current) return null;

  return (
    <LoadingContainer isLoading={!hasCurrentMetrics && !isInViewMode}>
      <Layout
        direction="column"
        align="left"
        alignSelf="center"
        spacing={12}
        mt={isInViewMode ? 8 : 0}
      >
        {!isInViewMode && (
          <Card mode="bordered">
            <Layout spacing={4}>
              <Layout direction="column" spacing={4}>
                <Text variant="subtitle">{TRAINING_STAGES[stage]?.title}</Text>
                {TRAINING_STAGES[stage]?.scoreDescription.map(
                  (description, index) =>
                    index <
                    TRAINING_STAGES[stage]?.scoreDescription.length - 1 ? (
                      <Text>{description}</Text>
                    ) : (
                      <Text>
                        {description}
                        {TRAINING_STAGES[stage]?.canAdjustPredictionFilters ? (
                          <Link onClick={show}>
                            {intl.formatMessage({
                              id: 'GuidedAiStatus.adjustFiltersLink',
                              defaultMessage: 'adjust your filters.',
                              description: 'Link text for adjusting filters',
                            })}
                          </Link>
                        ) : null}
                      </Text>
                    ),
                )}
                <Text>
                  <FormattedMessage
                    id="GuidedAiStatus.trainingRoundLabel"
                    defaultMessage="<strong>Training Round:</strong> {trainingRound}"
                    description="Label for training round stage. Example: 'Training Round: Initial Training'"
                    values={{
                      trainingRound: TRAINING_STAGES[stage]?.trainingRound,
                      strong: (chunks) => <strong>{chunks}</strong>,
                    }}
                  />
                </Text>
              </Layout>
            </Layout>
          </Card>
        )}
        {ACCURACY_METRIC_TRAINING_STAGES.includes(model.stage) && (
          <AccuracyMetrics />
        )}
      </Layout>
      {modal}
    </LoadingContainer>
  );
};

export default GuidedAiStatus;
